import React, { useMemo, useRef } from 'react';
import { Button, Col, Form, Input, notification, Row, Select, Space, Typography } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppSelect } from '../../../../constants/appSelect';
import { CloseOutlined, ContainerOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  getAllBlocksVideoRequest,
  getAllDisplayPageRequest,
} from '../../../../services/lichvietvideo/commonServiceRequest';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { createPositionVideoRequest } from '../../../../services/lichvietvideo/positionVideoServiceRequest';

const { Text, Title } = Typography;

const CreatePositionVideo = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const watchBlockIds = Form.useWatch('block_ids', form);
  const listBlockRef = useRef(null);

  // get data all blocks video
  const { data: dataAllBlockVideos } = useQuery({
    queryKey: ['dataAllBlockVideos'],
    queryFn: () => getAllBlocksVideoRequest(),
  });

  // convert list blocks video options
  const dataAllBlocksVideoOption = useMemo(() => {
    const selected = watchBlockIds?.map((item) => item?.block_id) || [];
    return dataAllBlockVideos?.data
      ?.filter((item) => item?.status === 1)
      ?.map((item) => {
        return { label: item?.name, value: item?.id, disabled: selected?.includes(item?.id) };
      });
  }, [dataAllBlockVideos, watchBlockIds]);

  // get data all display page
  const { data: dataAllDisplayPages } = useQuery({
    queryKey: ['dataAllDisplayPages'],
    queryFn: () => getAllDisplayPageRequest(),
  });

  // convert list position options
  const dataAllDisplayPagesOption = useMemo(() => {
    return dataAllDisplayPages?.data?.map((item) => {
      return { label: item?.name, value: item?.name };
    });
  }, [dataAllDisplayPages]);

  // handle submit form
  const { mutate: mutateCreatePositionVideo, isPending } = useMutation({
    mutationFn: createPositionVideoRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Thêm mới vị trí hiển thị thành công!',
        });
        navigate(-1);
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (data) => {
    const dataSubmit = {
      display_page: data?.display_page,
      block_ids: data?.block_ids ? data?.block_ids?.map((item) => item?.block_id) : [],
      status: data?.status,
    };
    mutateCreatePositionVideo(dataSubmit);
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    if (source.index === destination.index) return;

    const current = form.getFieldValue('block_ids');
    const reordered = reorder(current, source.index, destination.index);
    form.setFieldsValue({ block_ids: reordered });
  };

  return (
    <Form
      name="create-position-video"
      form={form}
      layout="vertical"
      className="mb-5"
      onFinish={handleSubmitForm}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Thêm vị trí hiển thị</Title>
        </Col>

        <Col xs={24}>
          <Form.Item
            name="display_page"
            label={
              <Text>
                Vị trí hiển thị <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn vị trí hiển thị' }]}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn vị trí hiển thị"
              options={dataAllDisplayPagesOption}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable-selects">
              {(provided) => (
                <Form.List name="block_ids">
                  {(fields, { add, remove }) => (
                    <>
                      <div className="flex flex-row justify-between items-center">
                        <Text className="mb-4">Danh sách khối</Text>
                        <Form.Item>
                          <Button
                            type="primary"
                            onClick={() => {
                              add('', 0);
                              listBlockRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                          >
                            Thêm khối
                          </Button>
                        </Form.Item>
                      </div>
                      <div
                        ref={(el) => {
                          provided.innerRef(el);
                          listBlockRef.current = el;
                        }}
                        {...provided.droppableProps}
                         className={`max-h-[428px] overflow-y-auto p-2 ${
                          fields?.length > 0 ? 'border border-[#d9d9d9] rounded-lg mb-4' : ''
                        }`}
                      >
                        <>
                          {fields.map((field, index) => (
                            <Draggable key={field.key.toString()} draggableId={field.key.toString()} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    background: snapshot.isDragging ? '#f0f5ff' : '#fff',
                                    padding: 16,
                                    marginBottom: 8,
                                    border: '1px solid #d9d9d9',
                                    borderRadius: 8,
                                    display: 'flex',
                                    alignItems: 'start',
                                    justifyContent: 'between',
                                    gap: 8,
                                    ...provided.draggableProps.style,
                                    cursor: 'grab',
                                  }}
                                >
                                  <Form.Item
                                    {...field}
                                    name={[field.name, 'block_id']}
                                    fieldKey={[field.fieldKey, 'block_id']}
                                    className="flex-1 min-w-0 mb-0"
                                    rules={[{ required: true, message: 'Vui lòng chọn khối' }]}
                                  >
                                    <Select
                                      placeholder="Chọn khối"
                                      options={dataAllBlocksVideoOption}
                                      style={{
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    />
                                  </Form.Item>

                                  <Button type="text" danger onClick={() => remove(index)}>
                                    <DeleteOutlined className="text-xl" />
                                  </Button>
                                </div>
                              )}
                            </Draggable>
                          ))}
                        </>
                      </div>
                    </>
                  )}
                </Form.List>
              )}
            </Droppable>
          </DragDropContext>
        </Col>

        <Col xs={24}>
          <Form.Item
            name="status"
            label={
              <Text>
                Trạng thái <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
            initialValue={1}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CreatePositionVideo;
